/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Chip,
  Tooltip,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Avatar,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  LinearProgress,
} from '@mui/material';
import {
  Close,
  Add,
  Edit,
  Delete,
  ArrowBack,
  ArrowForward,
  History,
  Person,
  CalendarMonth,
  ViewColumn,
  Save,
  BarChart,
  ViewKanban,
  EditNote,
  Today,
  Store,
  Send,
  ChatBubbleOutline,
  Visibility,
  Lock,
  LockOpen,
  Public,
  Search,
  ContentCopy,
  CheckCircleOutline,
  PictureAsPdf,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const API = '/api';

const PRIORIDADE_CORES = {
  baixa: '#4caf50',
  normal: '#2196f3',
  alta: '#ff9800',
  urgente: '#f44336',
};

const PRIORIDADE_OPTIONS = [
  { value: 'baixa', label: 'Baixa' },
  { value: 'normal', label: 'Normal' },
  { value: 'alta', label: 'Alta' },
  { value: 'urgente', label: 'Urgente' },
];

const STATUS_OPTIONS = [
  { value: 'pendente', label: 'Pendente' },
  { value: 'em_andamento', label: 'Em andamento' },
  { value: 'concluido', label: 'Concluído' },
  { value: 'bloqueado', label: 'Bloqueado' },
];

const COLUNAS_DEFAULT = [
  { titulo: 'Novas Solicitações', cor: '#e3f2fd' },
  { titulo: 'Em Andamento', cor: '#fff3e0' },
  { titulo: 'Em Execução', cor: '#e8f5e9' },
  { titulo: 'Aguardando Aprovação', cor: '#f3e5f5' },
  { titulo: 'Monitoramento', cor: '#fce4ec' },
  { titulo: 'Concluído', cor: '#e0f2f1' },
];

const CATEGORIAS = [
  'Aço', 'Aluguel de Equipamentos', 'Ar condicionado', 'Argamassa', 'Avarias', 'Cerâmica', 'Civil',
  'Concreto', 'Demolição', 'Elétrica', 'Elevador', 'Estrutura Metálica', 'Ferragens', 'Forro', 'Frete', 'Fundação', 'Gerador', 'Gesso',
  'Hidráulica', 'Incêndio', 'Impermeabilização', 'Locação', 'Madeira', 'Mão de Obra',
  'Materiais de Construção', 'Mármores e Granitos', 'Mobiliários', 'Paisagismo',
  'Pintura', 'Pisos e Revestimentos', 'Porta Pallets', 'Portas Rápidas', 'Projetos', 'Projetos Complementares', 'Refrigeração',
  'Serralheria', 'Sinistro', 'T.I', 'Terraplanagem', 'Tintas', 'Vidros', 'Outros',
];

const LOJA_ORDER = [
  'PEREQUE LOJA 01','COTIA LOJA 02','GUARUJA LOJA 03','PERUIBE LOJA 04',
  'PRAIA GRANDE LOJA 05','SANTOS LOJA 06','PIRAJUSSARA LOJA 07','ITANHAEM LOJA 08',
  'MBOI MIRIM LOJA 09','MONGAGUA LOJA 10','MORUMBI LOJA 11','REGISTRO LOJA 12',
  'ENSEADA LOJA 13','BERTIOGA LOJA 14','BARUERI LOJA 15','CAMPO LIMPO LOJA 16',
  'SUZANO LOJA 17','00-MARINA GUARUJA','MATRIZ','CEASA-TOP FRUIT','CAMPO LIMPO - GALPÃO',
];

const lojaOrder = (nome) => {
  const idx = LOJA_ORDER.findIndex(n => nome?.toUpperCase().includes(n.toUpperCase()) || n.toUpperCase().includes(nome?.toUpperCase()));
  return idx === -1 ? 999 : idx;
};

const GANTT_CORES = [
  '#00838f','#006064','#0277bd','#01579b','#1565c0','#283593',
  '#6a1b9a','#4527a0','#2e7d32','#558b2f','#e65100','#bf360c',
];

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('pt-BR');
};

// Converte datas ISO dentro de textos de histórico para dd/mm/yyyy
const fmtDetalhes = (text) => {
  if (!text) return text;
  return text.replace(/(\d{4}-\d{2}-\d{2})/g, (_, d) => {
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y}`;
  });
};

const diffDays = (a, b) => {
  if (!a || !b) return 0;
  return Math.max(0, Math.round((new Date(b + 'T00:00:00') - new Date(a + 'T00:00:00')) / 86400000) + 1);
};

// ── Gantt Chart ──────────────────────────────────────────────────────────────
function GanttChart({ colunas, zoom }) {
  const cards = useMemo(() => {
    const all = [];
    colunas.forEach(col => {
      (col.cards || []).forEach(c => {
        if (c.data_inicio && c.data_termino) {
          all.push({ ...c, coluna_titulo: col.titulo });
        }
      });
    });
    return all.sort((a, b) => new Date(a.data_inicio) - new Date(b.data_inicio));
  }, [colunas]);

  const { days, weeks } = useMemo(() => {
    if (!cards.length) return { days: [], weeks: [] };
    let min = new Date(cards[0].data_inicio + 'T00:00:00');
    let max = new Date(cards[0].data_termino + 'T00:00:00');
    cards.forEach(c => {
      const s = new Date(c.data_inicio + 'T00:00:00');
      const e = new Date(c.data_termino + 'T00:00:00');
      if (s < min) min = s;
      if (e > max) max = e;
    });
    // Pad to start of week and end of week
    const start = new Date(min);
    start.setDate(start.getDate() - start.getDay()); // Sunday of first week
    const end = new Date(max);
    const daysToEnd = 6 - end.getDay();
    end.setDate(end.getDate() + daysToEnd + 1);

    const days = [];
    const d = new Date(start);
    while (d < end) { days.push(new Date(d)); d.setDate(d.getDate() + 1); }

    // Group by week+month for header
    const weeks = [];
    let i = 0;
    while (i < days.length) {
      const monthLabel = days[i].toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
      const startI = i;
      while (i < days.length && days[i].toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }) === monthLabel) i++;
      weeks.push({ label: monthLabel, count: i - startI, startIdx: startI });
    }
    return { days, weeks };
  }, [cards]);

  if (!cards.length) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Nenhum card com Data Início e Data Término definidas. Edite os cards para incluí-los no Gantt.
      </Alert>
    );
  }

  const DAY_W = zoom;
  const DAY_INITIALS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

  return (
    <Box sx={{ overflowX: 'auto', mt: 1 }}>
      <table style={{ borderCollapse: 'collapse', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
        <thead>
          {/* Month header */}
          <tr>
            <th style={{ width: 30, border: '1px solid #b0bec5', background: '#1a237e', color: '#fff', padding: '4px 6px' }}>#</th>
            <th style={{ minWidth: 160, border: '1px solid #b0bec5', background: '#1a237e', color: '#fff', padding: '4px 8px' }}>TAREFA</th>
            <th style={{ width: 85, border: '1px solid #b0bec5', background: '#1a237e', color: '#fff', padding: '4px 6px' }}>INÍCIO</th>
            <th style={{ width: 55, border: '1px solid #b0bec5', background: '#1a237e', color: '#fff', padding: '4px 6px' }}>DIAS</th>
            <th style={{ width: 85, border: '1px solid #b0bec5', background: '#1a237e', color: '#fff', padding: '4px 6px' }}>TÉRMINO</th>
            <th style={{ minWidth: 110, border: '1px solid #b0bec5', background: '#1a237e', color: '#fff', padding: '4px 8px' }}>PONTO FOCAL</th>
            {weeks.map((w, wi) => (
              <th key={wi} colSpan={w.count} style={{ border: '1px solid #b0bec5', background: '#1565c0', color: '#fff', padding: '3px 0', textAlign: 'center', textTransform: 'capitalize' }}>
                {w.label}
              </th>
            ))}
          </tr>
          {/* Day numbers */}
          <tr>
            <th colSpan={6} style={{ border: '1px solid #b0bec5', background: '#e3f2fd' }} />
            {days.map((d, di) => {
              const isSun = d.getDay() === 0;
              const isSat = d.getDay() === 6;
              return (
                <th key={di} style={{
                  width: DAY_W, minWidth: DAY_W, border: '1px solid #b0bec5',
                  background: isSun || isSat ? '#ffecb3' : '#e3f2fd',
                  color: '#37474f', padding: '2px 0', textAlign: 'center', fontWeight: 600,
                }}>
                  {d.getDate()}
                </th>
              );
            })}
          </tr>
          {/* Day initials */}
          <tr>
            <th colSpan={6} style={{ border: '1px solid #b0bec5', background: '#e3f2fd' }} />
            {days.map((d, di) => {
              const isSun = d.getDay() === 0;
              const isSat = d.getDay() === 6;
              return (
                <th key={di} style={{
                  width: DAY_W, minWidth: DAY_W, border: '1px solid #b0bec5',
                  background: isSun || isSat ? '#ffecb3' : '#e3f2fd',
                  color: '#546e7a', padding: '2px 0', textAlign: 'center', fontWeight: 500,
                  fontSize: '0.65rem',
                }}>
                  {DAY_INITIALS[d.getDay()]}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {cards.map((card, idx) => {
            const cor = GANTT_CORES[idx % GANTT_CORES.length];
            const ini = new Date(card.data_inicio + 'T00:00:00');
            const fim = new Date(card.data_termino + 'T00:00:00');
            const dias = diffDays(card.data_inicio, card.data_termino);
            const rowBg = idx % 2 === 0 ? '#ffffff' : '#f5f5f5';
            return (
              <tr key={card.id}>
                <td style={{ border: '1px solid #cfd8dc', background: rowBg, textAlign: 'center', padding: '4px 2px', fontWeight: 700, color: '#37474f' }}>
                  {idx + 1}
                </td>
                <td style={{ border: '1px solid #cfd8dc', background: rowBg, padding: '4px 8px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: cor, flexShrink: 0 }} />
                    <span style={{ fontWeight: 600 }}>{card.titulo}</span>
                  </Box>
                  <div style={{ fontSize: '0.65rem', color: '#78909c', marginTop: 1 }}>{card.coluna_titulo}</div>
                </td>
                <td style={{ border: '1px solid #cfd8dc', background: rowBg, textAlign: 'center', padding: '4px 4px' }}>{fmtDate(card.data_inicio)}</td>
                <td style={{ border: '1px solid #cfd8dc', background: rowBg, textAlign: 'center', padding: '4px 4px', fontWeight: 700 }}>{dias}</td>
                <td style={{ border: '1px solid #cfd8dc', background: rowBg, textAlign: 'center', padding: '4px 4px' }}>{fmtDate(card.data_termino)}</td>
                <td style={{ border: '1px solid #cfd8dc', background: rowBg, padding: '4px 8px' }}>{card.responsavel_nome || '—'}</td>
                {days.map((d, di) => {
                  const active = d >= ini && d <= fim;
                  const isSun = d.getDay() === 0;
                  const isSat = d.getDay() === 6;
                  return (
                    <td key={di} style={{
                      width: DAY_W, minWidth: DAY_W, border: '1px solid #cfd8dc',
                      background: active ? cor : (isSun || isSat) ? '#fff8e1' : rowBg,
                      padding: 0,
                    }} />
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </Box>
  );
}

// ── Gantt para Resumo Diário (múltiplas lojas) ───────────────────────────────
function GanttChartResumo({ grupos, zoom }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const cards = useMemo(() => {
    const all = [];
    grupos.forEach(g => {
      (g.cards || []).forEach(c => {
        if (c.data_inicio && c.data_termino) {
          all.push({ ...c, cliente_nome: g.cliente_nome });
        }
      });
    });
    return all.sort((a, b) => new Date(a.data_inicio) - new Date(b.data_inicio));
  }, [grupos]);

  const { days, weeks } = useMemo(() => {
    if (!cards.length) return { days: [], weeks: [] };
    let min = new Date(cards[0].data_inicio + 'T00:00:00');
    let max = new Date(cards[0].data_termino + 'T00:00:00');
    cards.forEach(c => {
      const s = new Date(c.data_inicio + 'T00:00:00');
      const e = new Date(c.data_termino + 'T00:00:00');
      if (s < min) min = s;
      if (e > max) max = e;
    });
    const start = new Date(min);
    start.setDate(start.getDate() - start.getDay());
    const end = new Date(max);
    end.setDate(end.getDate() + (6 - end.getDay()) + 1);
    const days = [];
    const d = new Date(start);
    while (d < end) { days.push(new Date(d)); d.setDate(d.getDate() + 1); }
    const weeks = [];
    let i = 0;
    while (i < days.length) {
      const label = days[i].toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
      const startI = i;
      while (i < days.length && days[i].toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }) === label) i++;
      weeks.push({ label, count: i - startI });
    }
    return { days, weeks };
  }, [cards]);

  if (!cards.length) return <Alert severity="info" sx={{ mt: 2 }}>Nenhum card com datas definidas para hoje.</Alert>;

  const DAY_W = zoom;
  const DAY_INITIALS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

  return (
    <Box sx={{ overflowX: 'auto', mt: 1 }}>
      <table style={{ borderCollapse: 'collapse', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
        <thead>
          <tr>
            <th style={{ width: 30, border: '1px solid #b0bec5', background: '#1a237e', color: '#fff', padding: '4px 6px' }}>#</th>
            <th style={{ minWidth: 130, border: '1px solid #b0bec5', background: '#1a237e', color: '#fff', padding: '4px 8px' }}>LOJA</th>
            <th style={{ minWidth: 150, border: '1px solid #b0bec5', background: '#1a237e', color: '#fff', padding: '4px 8px' }}>TAREFA</th>
            <th style={{ width: 85, border: '1px solid #b0bec5', background: '#1a237e', color: '#fff', padding: '4px 6px' }}>INÍCIO</th>
            <th style={{ width: 55, border: '1px solid #b0bec5', background: '#1a237e', color: '#fff', padding: '4px 6px' }}>DIAS</th>
            <th style={{ width: 85, border: '1px solid #b0bec5', background: '#1a237e', color: '#fff', padding: '4px 6px' }}>TÉRMINO</th>
            <th style={{ minWidth: 100, border: '1px solid #b0bec5', background: '#1a237e', color: '#fff', padding: '4px 8px' }}>PONTO FOCAL</th>
            {weeks.map((w, wi) => (
              <th key={wi} colSpan={w.count} style={{ border: '1px solid #b0bec5', background: '#1565c0', color: '#fff', padding: '3px 0', textAlign: 'center', textTransform: 'capitalize' }}>{w.label}</th>
            ))}
          </tr>
          <tr>
            <th colSpan={7} style={{ border: '1px solid #b0bec5', background: '#e3f2fd' }} />
            {days.map((d, di) => {
              const isToday = d.getTime() === today.getTime();
              const isWeekend = d.getDay() === 0 || d.getDay() === 6;
              return (
                <th key={di} style={{ width: DAY_W, minWidth: DAY_W, border: '1px solid #b0bec5', background: isToday ? '#ffcc02' : isWeekend ? '#ffecb3' : '#e3f2fd', color: '#37474f', padding: '2px 0', textAlign: 'center', fontWeight: isToday ? 800 : 600 }}>
                  {d.getDate()}
                </th>
              );
            })}
          </tr>
          <tr>
            <th colSpan={7} style={{ border: '1px solid #b0bec5', background: '#e3f2fd' }} />
            {days.map((d, di) => {
              const isToday = d.getTime() === today.getTime();
              const isWeekend = d.getDay() === 0 || d.getDay() === 6;
              return (
                <th key={di} style={{ width: DAY_W, minWidth: DAY_W, border: '1px solid #b0bec5', background: isToday ? '#ffcc02' : isWeekend ? '#ffecb3' : '#e3f2fd', color: '#546e7a', padding: '2px 0', textAlign: 'center', fontSize: '0.65rem' }}>
                  {DAY_INITIALS[d.getDay()]}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {cards.map((card, idx) => {
            const cor = GANTT_CORES[idx % GANTT_CORES.length];
            const ini = new Date(card.data_inicio + 'T00:00:00');
            const fim = new Date(card.data_termino + 'T00:00:00');
            const dias = diffDays(card.data_inicio, card.data_termino);
            const rowBg = idx % 2 === 0 ? '#ffffff' : '#f5f5f5';
            return (
              <tr key={card.id}>
                <td style={{ border: '1px solid #cfd8dc', background: rowBg, textAlign: 'center', padding: '4px 2px', fontWeight: 700, color: '#37474f' }}>{idx + 1}</td>
                <td style={{ border: '1px solid #cfd8dc', background: rowBg, padding: '4px 8px', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.72rem', color: '#1565c0', fontWeight: 600 }}>{card.cliente_nome}</td>
                <td style={{ border: '1px solid #cfd8dc', background: rowBg, padding: '4px 8px', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: cor, flexShrink: 0 }} />
                    <span style={{ fontWeight: 600 }}>{card.titulo}</span>
                  </Box>
                </td>
                <td style={{ border: '1px solid #cfd8dc', background: rowBg, textAlign: 'center', padding: '4px 4px' }}>{fmtDate(card.data_inicio)}</td>
                <td style={{ border: '1px solid #cfd8dc', background: rowBg, textAlign: 'center', padding: '4px 4px', fontWeight: 700 }}>{dias}</td>
                <td style={{ border: '1px solid #cfd8dc', background: rowBg, textAlign: 'center', padding: '4px 4px' }}>{fmtDate(card.data_termino)}</td>
                <td style={{ border: '1px solid #cfd8dc', background: rowBg, padding: '4px 8px' }}>{card.responsavel_nome || '—'}</td>
                {days.map((d, di) => {
                  const isToday = d.getTime() === today.getTime();
                  const active = d >= ini && d <= fim;
                  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                  return (
                    <td key={di} style={{ width: DAY_W, minWidth: DAY_W, border: '1px solid #cfd8dc', background: active ? cor : isToday ? '#fff9c4' : isWeekend ? '#fff8e1' : rowBg, padding: 0 }} />
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </Box>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function FluxoCronogramaModal({ open, onClose }) {
  const { user, token, isAdmin } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {};

  const [clientes, setClientes] = useState([]);
  const [selectedClienteId, setSelectedClienteId] = useState('');
  const [colunas, setColunas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [mainView, setMainView] = useState('resumo'); // 'resumo' | 'loja'
  const [viewMode, setViewMode] = useState('board');
  const [ganttZoom, setGanttZoom] = useState(26);
  const [searchTerm, setSearchTerm] = useState('');
  const [lojaStatusFilter, setLojaStatusFilter] = useState([]);
  const [resumoData, setResumoData] = useState([]);
  const [resumoLoading, setResumoLoading] = useState(false);
  const [resumoGanttZoom, setResumoGanttZoom] = useState(24);
  const [resumoView, setResumoView] = useState('cards'); // 'cards' | 'gantt'
  const [resumoFiltroCategoria, setResumoFiltroCategoria] = useState('');
  const [resumoFiltroStatus, setResumoFiltroStatus] = useState([]);
  const [resumoSearch, setResumoSearch] = useState('');
  const todayISO = new Date().toISOString().slice(0, 10);
  const [resumoData_str, setResumoDataStr] = useState(todayISO);
  const todayStr = new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
  const zoomIn  = () => setGanttZoom(z => Math.min(z + 4, 52));
  const zoomOut = () => setGanttZoom(z => Math.max(z - 4, 14));

  const colunasTitulosDisponiveis = useMemo(() => colunas.map(c => c.titulo), [colunas]);

  const colunasFiltradas = useMemo(() => {
    if (!lojaStatusFilter.length && !searchTerm) return colunas;
    return colunas
      .filter(col => !lojaStatusFilter.length || lojaStatusFilter.includes(col.titulo))
      .map(col => ({
        ...col,
        cards: (col.cards || []).filter(c =>
          !searchTerm ||
          c.titulo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.descricao?.toLowerCase().includes(searchTerm.toLowerCase())
        ),
      }));
  }, [colunas, lojaStatusFilter, searchTerm]);

  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [cardForm, setCardForm] = useState({
    titulo: '', descricao: '', prioridade: 'normal', status: 'pendente',
    data_inicio: '', data_termino: '', responsavel_id: '', coluna_id: null, categoria: '',
  });

  const [colunaModalOpen, setColunaModalOpen] = useState(false);
  const [editingColuna, setEditingColuna] = useState(null);
  const [colunaForm, setColunaForm] = useState({ titulo: '', cor: '#e0e0e0' });

  const [historicoModalOpen, setHistoricoModalOpen] = useState(false);
  const [historicoData, setHistoricoData] = useState([]);
  const [historicoLoading, setHistoricoLoading] = useState(false);
  const [comentarioText, setComentarioText] = useState('');
  const [comentarioLoading, setComentarioLoading] = useState(false);

  const [dragCard, setDragCard] = useState(null);
  const [dragOverColuna, setDragOverColuna] = useState(null);
  const [editingUser, setEditingUser] = useState(null);

  // ── Duplicar em lote ──────────────────────────────────────────────────────────
  const [duplicarModalOpen, setDuplicarModalOpen] = useState(false);
  const [duplicarCard, setDuplicarCard] = useState(null);
  const [duplicarClienteIds, setDuplicarClienteIds] = useState(new Set());
  const [duplicarLoading, setDuplicarLoading] = useState(false);
  const [duplicarProgress, setDuplicarProgress] = useState({ done: 0, total: 0 });

  // ── Privado ──────────────────────────────────────────────────────────────────
  const [privadoCards, setPrivadoCards] = useState({ meus: [], publicados: [] });
  const [privadoLoading, setPrivadoLoading] = useState(false);
  const [privadoModalOpen, setPrivadoModalOpen] = useState(false);
  const [editingPrivado, setEditingPrivado] = useState(null);
  const [privadoForm, setPrivadoForm] = useState({
    titulo: '', descricao: '', prioridade: 'normal', status: 'pendente',
    data_inicio: '', data_termino: '', categoria: '',
  });
  const [privadoComentarios, setPrivadoComentarios] = useState([]);
  const [privadoComentariosLoading, setPrivadoComentariosLoading] = useState(false);

  // ── Concluído ─────────────────────────────────────────────────────────────────
  const [concluidoData, setConcluidoData] = useState([]);
  const [concluidoLoading, setConcluidoLoading] = useState(false);
  const [concluidoDataInicio, setConcluidoDataInicio] = useState('');
  const [concluidoDataFim, setConcluidoDataFim] = useState('');
  const [concluidoClienteId, setConcluidoClienteId] = useState('');
  const [privadoComentarioText, setPrivadoComentarioText] = useState('');
  const [privadoComentarioSending, setPrivadoComentarioSending] = useState(false);

  const fetchResumo = useCallback(async (dateStr) => {
    if (!token) return;
    setResumoLoading(true);
    try {
      const d = dateStr || resumoData_str || todayISO;
      const res = await fetch(`${API}/fluxo-cronograma/resumo-diario?data=${d}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setResumoData(await res.json());
      else setResumoData([]);
    } catch { setResumoData([]); }
    finally { setResumoLoading(false); }
  }, [token, resumoData_str, todayISO]);

  const fetchConcluidos = useCallback(async ({ dataInicio, dataFim, clienteId } = {}) => {
    if (!token) return;
    setConcluidoLoading(true);
    try {
      const params = new URLSearchParams();
      const di = dataInicio !== undefined ? dataInicio : concluidoDataInicio;
      const df = dataFim !== undefined ? dataFim : concluidoDataFim;
      const cid = clienteId !== undefined ? clienteId : concluidoClienteId;
      if (di) params.set('data_inicio', di);
      if (df) params.set('data_fim', df);
      if (cid) params.set('cliente_id', cid);
      const res = await fetch(`${API}/fluxo-cronograma/concluidos?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setConcluidoData(await res.json());
      else setConcluidoData([]);
    } catch { setConcluidoData([]); }
    finally { setConcluidoLoading(false); }
  }, [token, concluidoDataInicio, concluidoDataFim, concluidoClienteId]);

  useEffect(() => {
    if (!open || !token) return;
    fetchResumo();
    (async () => {
      try {
        let clienteIds = null;
        try {
          const meRes = await fetch(`${API}/me/clientes`, { headers: { Authorization: `Bearer ${token}` } });
          if (meRes.ok) {
            const meData = await meRes.json();
            clienteIds = meData.clientes || [];
          }
        } catch { /* ignore */ }

        const res = await fetch(`${API}/clientes`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          let data = await res.json();
          if (clienteIds && clienteIds.length > 0) {
            data = data.filter(c => clienteIds.map(Number).includes(Number(c.id)));
          }
          setClientes(data);
          if (data.length === 1) setSelectedClienteId(data[0].id);
        }
      } catch { /* ignore */ }

      try {
        const res = await fetch(`${API}/usuarios`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          setUsuarios(Array.isArray(data) ? data.filter(u => u.ativo !== false) : []);
        }
      } catch { /* ignore */ }
    })();
  }, [open, token, fetchResumo]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchColunas = useCallback(async () => {
    if (!selectedClienteId || !token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/fluxo-cronograma/${selectedClienteId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setColunas(await res.json());
      else setColunas([]);
    } catch { setColunas([]); }
    finally { setLoading(false); }
  }, [selectedClienteId, token]);

  useEffect(() => { if (selectedClienteId) fetchColunas(); }, [selectedClienteId, fetchColunas]);

  useEffect(() => { if (mainView === 'concluido') fetchConcluidos(); }, [mainView]); // eslint-disable-line react-hooks/exhaustive-deps

  const inicializarColunas = async () => {
    if (!selectedClienteId) return;
    for (let i = 0; i < COLUNAS_DEFAULT.length; i++) {
      try {
        await fetch(`${API}/fluxo-cronograma/colunas`, {
          method: 'POST', headers,
          body: JSON.stringify({ cliente_id: selectedClienteId, titulo: COLUNAS_DEFAULT[i].titulo, posicao: i, cor: COLUNAS_DEFAULT[i].cor }),
        });
      } catch { /* ignore */ }
    }
    fetchColunas();
    setSnack({ open: true, message: 'Colunas padrão criadas!', severity: 'success' });
  };

  // ── Coluna handlers ──────────────────────────────────────────────────────
  const openNewColuna = () => { setEditingColuna(null); setColunaForm({ titulo: '', cor: '#e0e0e0' }); setColunaModalOpen(true); };
  const openEditColuna = (col) => { setEditingColuna(col); setColunaForm({ titulo: col.titulo, cor: col.cor || '#e0e0e0' }); setColunaModalOpen(true); };

  const saveColuna = async () => {
    if (!colunaForm.titulo.trim()) { setSnack({ open: true, message: 'Título obrigatório', severity: 'warning' }); return; }
    try {
      if (editingColuna) {
        const res = await fetch(`${API}/fluxo-cronograma/colunas/${editingColuna.id}`, { method: 'PUT', headers, body: JSON.stringify(colunaForm) });
        if (!res.ok) throw new Error();
      } else {
        const res = await fetch(`${API}/fluxo-cronograma/colunas`, { method: 'POST', headers, body: JSON.stringify({ ...colunaForm, cliente_id: selectedClienteId }) });
        if (!res.ok) throw new Error();
      }
      setColunaModalOpen(false);
      fetchColunas();
      setSnack({ open: true, message: editingColuna ? 'Coluna atualizada!' : 'Coluna criada!', severity: 'success' });
    } catch { setSnack({ open: true, message: 'Erro ao salvar coluna', severity: 'error' }); }
  };

  const deleteColuna = async (col) => {
    if (!window.confirm(`Excluir a coluna "${col.titulo}" e todos os cards?`)) return;
    try {
      const res = await fetch(`${API}/fluxo-cronograma/colunas/${col.id}`, { method: 'DELETE', headers });
      if (res.ok || res.status === 204) { fetchColunas(); setSnack({ open: true, message: 'Coluna excluída', severity: 'success' }); }
      else { const d = await res.json().catch(() => ({})); setSnack({ open: true, message: d.error || 'Erro ao excluir', severity: 'error' }); }
    } catch { setSnack({ open: true, message: 'Erro ao excluir coluna', severity: 'error' }); }
  };

  // ── Card handlers ────────────────────────────────────────────────────────
  const colunaExecucao  = colunas.find(c => c.titulo.toLowerCase().includes('execu'));
  const colunaConcluida = colunas.find(c => c.titulo.toLowerCase().includes('conclu'));

  const openNewCard = (colunaId) => {
    setEditingCard(null);
    setCardForm({ titulo: '', descricao: '', prioridade: 'normal', status: 'pendente', data_inicio: '', data_termino: '', responsavel_id: '', coluna_id: colunaId, categoria: '' });
    setEditingUser({ nome: user?.nome });
    setCardModalOpen(true);
  };

  const openEditCard = (card) => {
    setEditingCard(card);
    setCardForm({
      titulo: card.titulo || '',
      descricao: card.descricao || '',
      prioridade: card.prioridade || 'normal',
      status: card.status || 'pendente',
      data_inicio: card.data_inicio || '',
      data_termino: card.data_termino || '',
      responsavel_id: card.responsavel_id || '',
      coluna_id: card.coluna_id,
      categoria: card.categoria || '',
    });
    setEditingUser({ nome: user?.nome });
    // Carrega histórico ao abrir edição
    openHistorico(card);
    setCardModalOpen(true);
  };

  const saveCard = async () => {
    if (!cardForm.titulo.trim()) { setSnack({ open: true, message: 'O título é obrigatório', severity: 'warning' }); return; }
    if (!editingCard && (!cardForm.data_inicio || !cardForm.data_termino)) {
      setSnack({ open: true, message: 'Data de Início e Término são obrigatórias para novos cards', severity: 'warning' });
      return;
    }
    try {
      const moverParaExecucao  = cardForm.status === 'em_andamento' && colunaExecucao;
      const moverParaConcluido = cardForm.status === 'concluido'    && colunaConcluida;
      const colunaDestino = moverParaConcluido ? colunaConcluida : moverParaExecucao ? colunaExecucao : null;

      if (editingCard) {
        const res = await fetch(`${API}/fluxo-cronograma/cards/${editingCard.id}`, {
          method: 'PUT', headers,
          body: JSON.stringify({
            titulo: cardForm.titulo,
            descricao: cardForm.descricao,
            prioridade: cardForm.prioridade,
            status: cardForm.status,
            categoria: cardForm.categoria || null,
            data_inicio: cardForm.data_inicio || null,
            data_termino: cardForm.data_termino || null,
            responsavel_id: cardForm.responsavel_id || null,
          }),
        });
        if (!res.ok) throw new Error();
        if (colunaDestino && editingCard.coluna_id !== colunaDestino.id) {
          await fetch(`${API}/fluxo-cronograma/cards/${editingCard.id}/move`, {
            method: 'PATCH', headers,
            body: JSON.stringify({ coluna_id: colunaDestino.id, posicao: 0 }),
          });
        }
      } else {
        const colunaId = colunaDestino ? colunaDestino.id : cardForm.coluna_id;
        const res = await fetch(`${API}/fluxo-cronograma/cards`, {
          method: 'POST', headers,
          body: JSON.stringify({
            ...cardForm,
            coluna_id: colunaId,
            cliente_id: selectedClienteId,
            data_inicio: cardForm.data_inicio || null,
            data_termino: cardForm.data_termino || null,
            responsavel_id: cardForm.responsavel_id || null,
          }),
        });
        if (!res.ok) throw new Error();
      }
      setCardModalOpen(false);
      setEditingUser(null);
      fetchColunas();
      fetchResumo();
      const movedMsg = colunaDestino ? ` → movido para "${colunaDestino.titulo}"` : '';
      setSnack({ open: true, message: (editingCard ? 'Card atualizado!' : 'Card criado!') + movedMsg, severity: 'success' });
    } catch { setSnack({ open: true, message: 'Erro ao salvar card', severity: 'error' }); }
  };

  const deleteCard = async (card) => {
    if (!window.confirm(`Excluir o card "${card.titulo}"?`)) return;
    try {
      const res = await fetch(`${API}/fluxo-cronograma/cards/${card.id}`, { method: 'DELETE', headers });
      if (res.ok || res.status === 204) { fetchColunas(); setSnack({ open: true, message: 'Card excluído', severity: 'success' }); }
      else { const d = await res.json().catch(() => ({})); setSnack({ open: true, message: d.error || 'Erro ao excluir', severity: 'error' }); }
    } catch { setSnack({ open: true, message: 'Erro ao excluir card', severity: 'error' }); }
  };

  const openDuplicar = (card) => {
    setDuplicarCard(card);
    setDuplicarClienteIds(new Set());
    setDuplicarModalOpen(true);
  };

  const toggleDuplicarCliente = (id) => {
    setDuplicarClienteIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const executarDuplicacao = async () => {
    if (!duplicarCard || duplicarClienteIds.size === 0) return;
    setDuplicarLoading(true);
    const ids = Array.from(duplicarClienteIds);
    setDuplicarProgress({ done: 0, total: ids.length });
    let ok = 0;
    let erros = 0;
    for (const clienteId of ids) {
      try {
        const colRes = await fetch(`${API}/fluxo-cronograma/colunas?cliente_id=${clienteId}`, { headers });
        const cols = colRes.ok ? await colRes.json() : [];
        const primeiraColuna = Array.isArray(cols) ? cols[0] : null;
        if (!primeiraColuna) { erros++; setDuplicarProgress(p => ({ ...p, done: p.done + 1 })); continue; }
        const res = await fetch(`${API}/fluxo-cronograma/cards`, {
          method: 'POST', headers,
          body: JSON.stringify({
            titulo: duplicarCard.titulo,
            descricao: duplicarCard.descricao || '',
            prioridade: duplicarCard.prioridade || 'normal',
            status: 'pendente',
            data_inicio: duplicarCard.data_inicio || null,
            data_termino: duplicarCard.data_termino || null,
            responsavel_id: duplicarCard.responsavel_id || null,
            categoria: duplicarCard.categoria || '',
            coluna_id: primeiraColuna.id,
            cliente_id: clienteId,
          }),
        });
        if (res.ok) ok++; else erros++;
      } catch { erros++; }
      setDuplicarProgress(p => ({ ...p, done: p.done + 1 }));
    }
    setDuplicarLoading(false);
    setDuplicarModalOpen(false);
    fetchColunas();
    fetchResumo();
    setSnack({ open: true, message: `Duplicado para ${ok} cliente(s)${erros > 0 ? ` (${erros} erro(s))` : ''}`, severity: erros > 0 ? 'warning' : 'success' });
  };

  const handleDragStart = (card) => setDragCard(card);
  const handleDragOver = (e, colunaId) => { e.preventDefault(); setDragOverColuna(colunaId); };
  const handleDragLeave = () => setDragOverColuna(null);

  const handleDrop = async (e, targetColunaId) => {
    e.preventDefault();
    setDragOverColuna(null);
    if (!dragCard || dragCard.coluna_id === targetColunaId) { setDragCard(null); return; }
    try {
      const res = await fetch(`${API}/fluxo-cronograma/cards/${dragCard.id}/move`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ coluna_id: targetColunaId, posicao: 0 }),
      });
      if (res.ok) { fetchColunas(); setSnack({ open: true, message: 'Card movido!', severity: 'success' }); }
    } catch { /* ignore */ }
    setDragCard(null);
  };

  const moveCardDirection = async (card, direction) => {
    const colIdx = colunas.findIndex(c => c.id === card.coluna_id);
    const targetIdx = colIdx + direction;
    if (targetIdx < 0 || targetIdx >= colunas.length) return;
    const targetColuna = colunas[targetIdx];
    try {
      const res = await fetch(`${API}/fluxo-cronograma/cards/${card.id}/move`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ coluna_id: targetColuna.id, posicao: 0 }),
      });
      if (res.ok) { fetchColunas(); setSnack({ open: true, message: `Movido para "${targetColuna.titulo}"`, severity: 'success' }); }
    } catch { /* ignore */ }
  };

  // ── Privado handlers ────────────────────────────────────────────────────────
  const exportConcluidoPdf = () => {
    const total = concluidoData.reduce((s, g) => s + g.cards.length, 0);
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>Cronologia Concluída</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 12px; color: #222; }
  h1 { font-size: 18px; color: #1a237e; margin-bottom: 4px; }
  .sub { color: #666; font-size: 11px; margin-bottom: 16px; }
  .grupo { margin-bottom: 20px; }
  .grupo-header { background: #1a237e; color: #fff; padding: 6px 10px; border-radius: 4px 4px 0 0; font-weight: bold; font-size: 13px; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #e8eaf6; text-align: left; padding: 5px 8px; font-size: 11px; border-bottom: 2px solid #9fa8da; }
  td { padding: 4px 8px; border-bottom: 1px solid #e0e0e0; font-size: 11px; }
  tr:nth-child(even) td { background: #f5f5f5; }
  .badge { display: inline-block; padding: 1px 6px; border-radius: 10px; font-size: 10px; color: #fff; background: #2e7d32; }
  .total { margin-top: 16px; font-size: 13px; font-weight: bold; color: #1a237e; }
  @media print { body { margin: 0; } }
</style></head><body>
<h1>Cronologia Concluída</h1>
<p class="sub">Gerado em ${new Date().toLocaleString('pt-BR')}${concluidoDataInicio ? ` | De: ${fmtDate(concluidoDataInicio)}` : ''}${concluidoDataFim ? ` Até: ${fmtDate(concluidoDataFim)}` : ''}</p>
${concluidoData.map(g => `
<div class="grupo">
  <div class="grupo-header">${g.cliente_nome} (${g.cards.length} tarefas)</div>
  <table>
    <tr><th>Título</th><th>Categoria</th><th>Ponto Focal</th><th>Início</th><th>Término</th><th>Status</th></tr>
    ${g.cards.map(c => `<tr>
      <td>${c.titulo || '—'}</td>
      <td>${c.categoria || '—'}</td>
      <td>${c.responsavel_nome || '—'}</td>
      <td>${c.data_inicio ? new Date(c.data_inicio + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</td>
      <td>${c.data_termino ? new Date(c.data_termino + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</td>
      <td><span class="badge">Concluído</span></td>
    </tr>`).join('')}
  </table>
</div>`).join('')}
<p class="total">Total: ${total} tarefas concluídas</p>
</body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 400); }
  };

  const fetchPrivado = useCallback(async () => {
    if (!token) return;
    setPrivadoLoading(true);
    try {
      const res = await fetch(`${API}/fluxo-cronograma-privado`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setPrivadoCards(await res.json());
      else setPrivadoCards({ meus: [], publicados: [] });
    } catch { setPrivadoCards({ meus: [], publicados: [] }); }
    finally { setPrivadoLoading(false); }
  }, [token]);

  useEffect(() => { if (mainView === 'privado' && open) fetchPrivado(); }, [mainView, open, fetchPrivado]);

  const openNewPrivado = () => {
    setEditingPrivado(null);
    setPrivadoComentarios([]);
    setPrivadoComentarioText('');
    setPrivadoForm({ titulo: '', descricao: '', prioridade: 'normal', status: 'pendente', data_inicio: '', data_termino: '', categoria: '' });
    setPrivadoModalOpen(true);
  };

  const openEditPrivado = (card) => {
    setEditingPrivado(card);
    setPrivadoComentarios([]);
    setPrivadoComentarioText('');
    setPrivadoForm({
      titulo: card.titulo || '',
      descricao: card.descricao || '',
      prioridade: card.prioridade || 'normal',
      status: card.status || 'pendente',
      data_inicio: card.data_inicio || '',
      data_termino: card.data_termino || '',
      categoria: card.categoria || '',
    });
    setPrivadoModalOpen(true);
    fetchPrivadoComentarios(card.id);
  };

  const savePrivado = async () => {
    if (!privadoForm.titulo.trim()) { setSnack({ open: true, message: 'Título obrigatório', severity: 'warning' }); return; }
    try {
      const url = editingPrivado ? `${API}/fluxo-cronograma-privado/${editingPrivado.id}` : `${API}/fluxo-cronograma-privado`;
      const method = editingPrivado ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify({
        ...privadoForm,
        data_inicio: privadoForm.data_inicio || null,
        data_termino: privadoForm.data_termino || null,
      }) });
      if (!res.ok) throw new Error();
      setPrivadoModalOpen(false);
      fetchPrivado();
      setSnack({ open: true, message: editingPrivado ? 'Card atualizado!' : 'Card criado!', severity: 'success' });
    } catch { setSnack({ open: true, message: 'Erro ao salvar card privado', severity: 'error' }); }
  };

  const fetchPrivadoComentarios = useCallback(async (cardId) => {
    if (!cardId) return;
    setPrivadoComentariosLoading(true);
    try {
      const res = await fetch(`${API}/fluxo-cronograma-privado/${cardId}/comentarios`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setPrivadoComentarios(await res.json());
    } catch { /* ignore */ } finally { setPrivadoComentariosLoading(false); }
  }, [token]);

  const handleAddPrivadoComentario = async () => {
    const texto = privadoComentarioText.trim();
    if (!texto || !editingPrivado) return;
    setPrivadoComentarioSending(true);
    try {
      const res = await fetch(`${API}/fluxo-cronograma-privado/${editingPrivado.id}/comentario`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ texto }),
      });
      if (res.ok) {
        setPrivadoComentarioText('');
        fetchPrivadoComentarios(editingPrivado.id);
      }
    } catch { /* ignore */ } finally { setPrivadoComentarioSending(false); }
  };

  const deletePrivado = async (card) => {
    if (!window.confirm(`Excluir "${card.titulo}"?`)) return;
    try {
      const res = await fetch(`${API}/fluxo-cronograma-privado/${card.id}`, { method: 'DELETE', headers });
      if (res.ok || res.status === 204) { fetchPrivado(); setSnack({ open: true, message: 'Card excluído', severity: 'success' }); }
    } catch { setSnack({ open: true, message: 'Erro ao excluir', severity: 'error' }); }
  };

  const togglePublicado = async (card) => {
    try {
      const res = await fetch(`${API}/fluxo-cronograma-privado/${card.id}/publicar`, { method: 'PATCH', headers });
      if (res.ok) { fetchPrivado(); setSnack({ open: true, message: card.publicado ? 'Card tornado privado' : 'Card publicado para todos!', severity: 'success' }); }
    } catch { setSnack({ open: true, message: 'Erro ao alterar visibilidade', severity: 'error' }); }
  };

  const openHistorico = async (card) => {
    setHistoricoLoading(true);
    setHistoricoData([]);
    try {
      const res = await fetch(`${API}/fluxo-cronograma/cards/${card.id}/historico`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setHistoricoData(await res.json());
    } catch { /* ignore */ }
    setHistoricoLoading(false);
  };

  const handleAddComentario = async () => {
    const texto = comentarioText.trim();
    if (!texto || !editingCard) return;
    setComentarioLoading(true);
    try {
      const res = await fetch(`${API}/fluxo-cronograma/cards/${editingCard.id}/comentario`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ texto }),
      });
      if (res.ok) {
        setComentarioText('');
        await openHistorico(editingCard);
      }
    } catch { /* ignore */ }
    setComentarioLoading(false);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: { minHeight: '85vh' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#1a237e', color: '#fff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ViewColumn />
            <Typography variant="h6">Fluxo Cronograma</Typography>
            {editingUser && cardModalOpen && (
              <Chip label={`${editingUser.nome} editando`} size="small" sx={{ bgcolor: '#ffeb3b', color: '#000', fontWeight: 600, ml: 2 }} />
            )}
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2, bgcolor: '#f5f5f5' }}>
          {/* ── Linha 1: tabs + controles direita ── */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: mainView === 'resumo' ? 1 : 2 }}>
            <ToggleButtonGroup value={mainView} exclusive onChange={(_, v) => v && setMainView(v)} size="small">
              <ToggleButton value="resumo"><Today fontSize="small" sx={{ mr: 0.5 }} />Resumo Diário</ToggleButton>
              <ToggleButton value="loja"><Store fontSize="small" sx={{ mr: 0.5 }} />Por Loja</ToggleButton>
              <ToggleButton value="privado" sx={{ color: mainView === 'privado' ? '#7b1fa2' : undefined }}><Lock fontSize="small" sx={{ mr: 0.5 }} />Privado</ToggleButton>
              <ToggleButton value="concluido" sx={{ color: mainView === 'concluido' ? '#2e7d32' : undefined }}><CheckCircleOutline fontSize="small" sx={{ mr: 0.5 }} />Concluído</ToggleButton>
            </ToggleButtonGroup>

            {mainView === 'resumo' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>{todayStr}</Typography>
                <Tooltip title="Atualizar">
                  <IconButton size="small" onClick={fetchResumo}><History fontSize="small" /></IconButton>
                </Tooltip>
                {resumoView === 'gantt' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #bdbdbd', borderRadius: 1, overflow: 'hidden' }}>
                    <IconButton size="small" onClick={() => setResumoGanttZoom(z => Math.max(z - 4, 14))} disabled={resumoGanttZoom <= 14} sx={{ borderRadius: 0, px: 1.2 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', lineHeight: 1 }}>−</Typography>
                    </IconButton>
                    <Typography variant="caption" sx={{ px: 1, fontWeight: 600, color: 'text.secondary', userSelect: 'none' }}>{Math.round((resumoGanttZoom / 24) * 100)}%</Typography>
                    <IconButton size="small" onClick={() => setResumoGanttZoom(z => Math.min(z + 4, 52))} disabled={resumoGanttZoom >= 52} sx={{ borderRadius: 0, px: 1.2 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', lineHeight: 1 }}>+</Typography>
                    </IconButton>
                  </Box>
                )}
                <ToggleButtonGroup value={resumoView} exclusive onChange={(_, v) => v && setResumoView(v)} size="small">
                  <ToggleButton value="cards"><ViewKanban fontSize="small" sx={{ mr: 0.5 }} />Cards</ToggleButton>
                  <ToggleButton value="gantt"><BarChart fontSize="small" sx={{ mr: 0.5 }} />Gantt</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            {/* Controles da Loja */}
            {mainView === 'loja' && (
              <>
                <FormControl size="small" sx={{ minWidth: 250 }}>
                  <InputLabel>Cliente</InputLabel>
                  <Select value={selectedClienteId} label="Cliente" onChange={(e) => { setSelectedClienteId(e.target.value); setLojaStatusFilter([]); }}>
                    {clientes.map(c => <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>)}
                  </Select>
                </FormControl>
                {selectedClienteId && (
                  <>
                    <Button variant="outlined" startIcon={<Add />} onClick={openNewColuna} size="small">Nova Coluna</Button>
                    {colunas.length === 0 && !loading && (
                      <Button variant="contained" onClick={inicializarColunas} size="small">Criar Colunas Padrão</Button>
                    )}
                    <TextField
                      size="small"
                      placeholder="Buscar assunto..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{ minWidth: 200 }}
                      InputProps={{
                        startAdornment: <Search fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />,
                      }}
                    />
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        multiple
                        value={lojaStatusFilter}
                        label="Status"
                        onChange={(e) => setLojaStatusFilter(e.target.value)}
                        renderValue={(selected) =>
                          selected.length === 0 ? 'Todos' : selected.length === 1 ? selected[0] : `${selected.length} selecionados`
                        }
                      >
                        {colunasTitulosDisponiveis.map((titulo) => (
                          <MenuItem key={titulo} value={titulo} dense>
                            <Checkbox checked={lojaStatusFilter.includes(titulo)} size="small" />
                            <ListItemText primary={titulo} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}
                {selectedClienteId && colunas.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                    {viewMode === 'gantt' && (
                      <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #bdbdbd', borderRadius: 1, overflow: 'hidden' }}>
                        <IconButton size="small" onClick={zoomOut} disabled={ganttZoom <= 14} sx={{ borderRadius: 0, px: 1.2 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', lineHeight: 1 }}>−</Typography>
                        </IconButton>
                        <Typography variant="caption" sx={{ px: 1, fontWeight: 600, color: 'text.secondary', userSelect: 'none' }}>{Math.round((ganttZoom / 26) * 100)}%</Typography>
                        <IconButton size="small" onClick={zoomIn} disabled={ganttZoom >= 52} sx={{ borderRadius: 0, px: 1.2 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', lineHeight: 1 }}>+</Typography>
                        </IconButton>
                      </Box>
                    )}
                    <ToggleButtonGroup value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)} size="small">
                      <ToggleButton value="board"><ViewKanban fontSize="small" sx={{ mr: 0.5 }} />Board</ToggleButton>
                      <ToggleButton value="gantt"><BarChart fontSize="small" sx={{ mr: 0.5 }} />Gantt</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* ── Linha 2: filtros (só no resumo) ── */}
          {mainView === 'resumo' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <TextField
                type="date"
                size="small"
                label="Data"
                value={resumoData_str}
                onChange={(e) => {
                  setResumoDataStr(e.target.value);
                  if (e.target.value) fetchResumo(e.target.value);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160 }}
              />
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Filtrar por Categoria</InputLabel>
                <Select value={resumoFiltroCategoria} label="Filtrar por Categoria" onChange={(e) => setResumoFiltroCategoria(e.target.value)}>
                  <MenuItem value="">Todas as categorias</MenuItem>
                  {CATEGORIAS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField
                size="small"
                placeholder="Buscar assunto..."
                value={resumoSearch}
                onChange={(e) => setResumoSearch(e.target.value)}
                sx={{ minWidth: 200 }}
                InputProps={{
                  startAdornment: <Search fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />,
                }}
              />
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Filtrar por Status</InputLabel>
                <Select
                  multiple
                  value={resumoFiltroStatus}
                  label="Filtrar por Status"
                  onChange={(e) => setResumoFiltroStatus(e.target.value)}
                  renderValue={(selected) =>
                    selected.length === 0 ? 'Todos' : selected.length === 1
                      ? STATUS_OPTIONS.find(s => s.value === selected[0])?.label || selected[0]
                      : `${selected.length} selecionados`
                  }
                >
                  {STATUS_OPTIONS.map(s => (
                    <MenuItem key={s.value} value={s.value} dense>
                      <Checkbox checked={resumoFiltroStatus.includes(s.value)} size="small" />
                      <ListItemText primary={s.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* ── Resumo Diário ── */}
          {mainView === 'resumo' && (
            <>
              {resumoLoading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>}
              {!resumoLoading && resumoData.length === 0 && (
                <Alert severity="info">Nenhuma tarefa ativa para hoje em nenhuma loja.</Alert>
              )}
              {!resumoLoading && resumoData.length > 0 && resumoView === 'gantt' && (() => {
                const ganttFiltrado = resumoData
                  .map(g => ({ ...g, cards: g.cards.filter(c =>
                    (!resumoFiltroCategoria || c.categoria === resumoFiltroCategoria) &&
                    (!resumoFiltroStatus.length || resumoFiltroStatus.includes(c.status)) &&
                    (!resumoSearch || c.titulo?.toLowerCase().includes(resumoSearch.toLowerCase()) || c.descricao?.toLowerCase().includes(resumoSearch.toLowerCase()))
                  )}))
                  .filter(g => g.cards.length > 0)
                  .sort((a, b) => lojaOrder(a.cliente_nome) - lojaOrder(b.cliente_nome));
                return <GanttChartResumo grupos={ganttFiltrado} zoom={resumoGanttZoom} />;
              })()}
              {!resumoLoading && resumoData.length > 0 && resumoView === 'cards' && (() => {
                const resumoFiltrado = resumoData
                  .map(g => ({ ...g, cards: g.cards.filter(c =>
                    (!resumoFiltroCategoria || c.categoria === resumoFiltroCategoria) &&
                    (!resumoFiltroStatus.length || resumoFiltroStatus.includes(c.status)) &&
                    (!resumoSearch || c.titulo?.toLowerCase().includes(resumoSearch.toLowerCase()) || c.descricao?.toLowerCase().includes(resumoSearch.toLowerCase()))
                  )}))
                  .filter(g => g.cards.length > 0)
                  .sort((a, b) => lojaOrder(a.cliente_nome) - lojaOrder(b.cliente_nome));
                return (
                <Box>
                  {/* Sumário geral */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Paper sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#1a237e' }}>
                      <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>{resumoFiltrado.reduce((s, g) => s + (g.cards?.length || 0), 0)}</Typography>
                      <Typography variant="caption" sx={{ color: '#90caf9' }}>tarefas hoje</Typography>
                    </Paper>
                    <Paper sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#2e7d32' }}>
                      <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>{resumoData.length}</Typography>
                      <Typography variant="caption" sx={{ color: '#a5d6a7' }}>lojas ativas</Typography>
                    </Paper>
                  </Box>
                  {/* Cards por loja */}
                  {resumoFiltrado.map((grupo) => {
                    const porStatus = grupo.cards.reduce((acc, c) => {
                      acc[c.status || 'pendente'] = (acc[c.status || 'pendente'] || 0) + 1;
                      return acc;
                    }, {});
                    return (
                      <Paper key={grupo.cliente_id} sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
                        {/* Header da loja */}
                        <Box sx={{ px: 2, py: 1, bgcolor: '#1a237e', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Store sx={{ color: '#90caf9', fontSize: 18 }} />
                            <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700 }}>{grupo.cliente_nome}</Typography>
                            <Chip label={grupo.cards.length} size="small" sx={{ bgcolor: '#283593', color: '#fff', height: 20, fontSize: '0.7rem' }} />
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {Object.entries(porStatus).map(([st, cnt]) => (
                              <Chip key={st} label={`${st}: ${cnt}`} size="small" sx={{ height: 20, fontSize: '0.65rem',
                                bgcolor: st === 'concluido' ? '#2e7d32' : st === 'em_andamento' ? '#1565c0' : st === 'bloqueado' ? '#b71c1c' : '#e65100',
                                color: '#fff' }} />
                            ))}
                          </Box>
                        </Box>
                        {/* Grid de cards */}
                        <Box sx={{ p: 1.5 }}>
                          <Grid container spacing={1}>
                            {grupo.cards.map((card) => (
                              <Grid item xs={12} sm={6} md={4} key={card.id}>
                                <Card sx={{ borderLeft: `4px solid ${PRIORIDADE_CORES[card.prioridade] || '#2196f3'}`, '&:hover': { boxShadow: 3 }, cursor: 'pointer' }} onClick={() => openEditCard(card)}>
                                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>{card.titulo}</Typography>
                                      <Tooltip title="Visualizar / Editar">
                                        <Visibility sx={{ fontSize: 16, color: 'text.secondary', ml: 0.5, flexShrink: 0 }} />
                                      </Tooltip>
                                    </Box>
                                    {card.descricao && (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                        {card.descricao.length > 60 ? card.descricao.substring(0, 60) + '...' : card.descricao}
                                      </Typography>
                                    )}
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                                      <Chip label={PRIORIDADE_OPTIONS.find(p => p.value === card.prioridade)?.label || card.prioridade} size="small"
                                        sx={{ height: 18, fontSize: '0.62rem', bgcolor: PRIORIDADE_CORES[card.prioridade] || '#ccc', color: '#fff' }} />
                                      <Chip label={STATUS_OPTIONS.find(s => s.value === card.status)?.label || card.status || 'Pendente'} size="small"
                                        sx={{ height: 18, fontSize: '0.62rem',
                                          bgcolor: card.status === 'concluido' ? '#2e7d32' : card.status === 'em_andamento' ? '#1565c0' : card.status === 'bloqueado' ? '#b71c1c' : '#e65100',
                                          color: '#fff' }} />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <CalendarMonth sx={{ fontSize: 12, color: 'text.secondary' }} />
                                        <Typography variant="caption" color="text.secondary">{fmtDate(card.data_inicio)} → {fmtDate(card.data_termino)}</Typography>
                                      </Box>
                                      {card.responsavel_nome && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <Person sx={{ fontSize: 12, color: 'text.secondary' }} />
                                          <Typography variant="caption" color="text.secondary">{card.responsavel_nome}</Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  </CardContent>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
                );
              })()}
            </>
          )}

          {/* ── Por Loja ── */}
          {mainView === 'loja' && (
            <>
              {!selectedClienteId && <Alert severity="info">Selecione um cliente para visualizar o fluxo cronograma.</Alert>}
              {loading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>}

              {/* ── Board View ── */}
              {selectedClienteId && !loading && colunas.length > 0 && viewMode === 'board' && (
            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2, minHeight: 400 }}>
              {colunasFiltradas.map((col) => (
                <Paper
                  key={col.id}
                  elevation={dragOverColuna === col.id ? 6 : 1}
                  onDragOver={(e) => handleDragOver(e, col.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, col.id)}
                  sx={{
                    minWidth: 280, maxWidth: 320, flex: '0 0 280px',
                    bgcolor: col.cor || '#fafafa', borderRadius: 2,
                    display: 'flex', flexDirection: 'column',
                    border: dragOverColuna === col.id ? '2px dashed #1976d2' : '1px solid #e0e0e0',
                    transition: 'border 0.2s',
                  }}
                >
                  <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{col.titulo}</Typography>
                      <Chip label={(col.cards || []).length} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                    </Box>
                    <Box>
                      <Tooltip title="Editar coluna">
                        <IconButton size="small" onClick={() => openEditColuna(col)}><Edit fontSize="inherit" /></IconButton>
                      </Tooltip>
                      {isAdmin() && (
                        <Tooltip title="Excluir coluna">
                          <IconButton size="small" onClick={() => deleteColuna(col)} color="error"><Delete fontSize="inherit" /></IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ p: 1, flex: 1, overflowY: 'auto', maxHeight: 500 }}>
                    {(col.cards || []).map((card) => {
                      const semDatas = !card.data_inicio || !card.data_termino;
                      return (
                        <Card
                          key={card.id}
                          draggable
                          onDragStart={() => handleDragStart(card)}
                          sx={{
                            mb: 1.5, cursor: 'grab',
                            borderLeft: `4px solid ${PRIORIDADE_CORES[card.prioridade] || '#2196f3'}`,
                            '&:hover': { boxShadow: 4 },
                            opacity: dragCard?.id === card.id ? 0.5 : 1,
                            transition: 'opacity 0.2s, box-shadow 0.2s',
                          }}
                        >
                          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>{card.titulo}</Typography>
                              {semDatas && (
                                <Tooltip title="Datas não definidas — clique em editar para incluir no Gantt">
                                  <Box
                                    onClick={() => openEditCard(card)}
                                    sx={{ cursor: 'pointer', color: '#e65100', fontWeight: 800, fontSize: '1rem', ml: 0.5, lineHeight: 1, userSelect: 'none' }}
                                  >
                                    *
                                  </Box>
                                </Tooltip>
                              )}
                            </Box>
                            {card.descricao && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                {card.descricao.length > 80 ? card.descricao.substring(0, 80) + '...' : card.descricao}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                              <Chip
                                label={PRIORIDADE_OPTIONS.find(p => p.value === card.prioridade)?.label || card.prioridade}
                                size="small"
                                sx={{ height: 20, fontSize: '0.65rem', bgcolor: PRIORIDADE_CORES[card.prioridade] || '#ccc', color: '#fff' }}
                              />
                              {card.data_inicio && (
                                <Chip
                                  icon={<CalendarMonth sx={{ fontSize: '0.75rem !important' }} />}
                                  label={`${fmtDate(card.data_inicio)} → ${fmtDate(card.data_termino)}`}
                                  size="small"
                                  sx={{ height: 20, fontSize: '0.62rem' }}
                                />
                              )}
                            </Box>

                            {card.responsavel_nome && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <Person sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">{card.responsavel_nome}</Typography>
                              </Box>
                            )}
                            {card.atualizador_nome && (
                              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>
                                Atualizado por {card.atualizador_nome}
                              </Typography>
                            )}

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                              <Box>
                                <Tooltip title="Mover para esquerda">
                                  <IconButton size="small" onClick={() => moveCardDirection(card, -1)} disabled={colunas.findIndex(c => c.id === card.coluna_id) === 0}>
                                    <ArrowBack sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Mover para direita">
                                  <IconButton size="small" onClick={() => moveCardDirection(card, 1)} disabled={colunas.findIndex(c => c.id === card.coluna_id) === colunas.length - 1}>
                                    <ArrowForward sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              <Box>
                                <Tooltip title="Histórico">
                                  <IconButton size="small" onClick={() => openHistorico(card)}><History sx={{ fontSize: 14 }} /></IconButton>
                                </Tooltip>
                                <Tooltip title="Duplicar para clientes">
                                  <IconButton size="small" onClick={() => openDuplicar(card)}><ContentCopy sx={{ fontSize: 14 }} /></IconButton>
                                </Tooltip>
                                <Tooltip title="Editar">
                                  <IconButton size="small" onClick={() => openEditCard(card)}><Edit sx={{ fontSize: 14 }} /></IconButton>
                                </Tooltip>
                                {isAdmin() && (
                                  <Tooltip title="Excluir">
                                    <IconButton size="small" onClick={() => deleteCard(card)} color="error"><Delete sx={{ fontSize: 14 }} /></IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>

                  <Box sx={{ p: 1, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                    <Button fullWidth size="small" startIcon={<Add />} onClick={() => openNewCard(col.id)} sx={{ textTransform: 'none', justifyContent: 'flex-start', color: 'text.secondary' }}>
                      Adicionar tarefa
                    </Button>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}

              {/* ── Gantt View ── */}
              {selectedClienteId && !loading && colunas.length > 0 && viewMode === 'gantt' && (
                <GanttChart colunas={colunasFiltradas} zoom={ganttZoom} />
              )}
            </>
          )}

          {/* ── Privado ── */}
          {mainView === 'privado' && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Lock sx={{ color: '#7b1fa2' }} fontSize="small" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#7b1fa2' }}>Meu Espaço Privado</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Atualizar">
                    <IconButton size="small" onClick={fetchPrivado}><History fontSize="small" /></IconButton>
                  </Tooltip>
                  <Button variant="contained" size="small" startIcon={<Add />} onClick={openNewPrivado}
                    sx={{ bgcolor: '#7b1fa2', '&:hover': { bgcolor: '#6a1b9a' } }}>
                    Novo Card Privado
                  </Button>
                </Box>
              </Box>

              {privadoLoading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>}

              {!privadoLoading && (
                <>
                  {/* Meus cards */}
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1 }}>
                    Meus Cards ({privadoCards.meus?.length || 0})
                  </Typography>
                  {(!privadoCards.meus || privadoCards.meus.length === 0) && (
                    <Alert severity="info" sx={{ mb: 2 }}>Você não tem cards privados. Crie um clicando em "Novo Card Privado".</Alert>
                  )}
                  <Grid container spacing={1.5} sx={{ mb: 3 }}>
                    {(privadoCards.meus || []).map(card => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={card.id}>
                        <Card sx={{
                          borderLeft: `4px solid ${card.publicado ? '#43a047' : '#7b1fa2'}`,
                          '&:hover': { boxShadow: 4 },
                          bgcolor: card.publicado ? '#f1f8e9' : '#fce4ec',
                        }}>
                          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>{card.titulo}</Typography>
                              <Tooltip title={card.publicado ? 'Publicado (clique para tornar privado)' : 'Privado (clique para publicar)'}>
                                <IconButton size="small" onClick={() => togglePublicado(card)} sx={{ ml: 0.5, p: 0.3 }}>
                                  {card.publicado ? <Public sx={{ fontSize: 16, color: '#43a047' }} /> : <Lock sx={{ fontSize: 16, color: '#7b1fa2' }} />}
                                </IconButton>
                              </Tooltip>
                            </Box>
                            {card.descricao && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                {card.descricao.length > 80 ? card.descricao.substring(0, 80) + '...' : card.descricao}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                              <Chip label={PRIORIDADE_OPTIONS.find(p => p.value === card.prioridade)?.label || card.prioridade} size="small"
                                sx={{ height: 18, fontSize: '0.62rem', bgcolor: PRIORIDADE_CORES[card.prioridade] || '#ccc', color: '#fff' }} />
                              <Chip label={STATUS_OPTIONS.find(s => s.value === card.status)?.label || card.status} size="small"
                                sx={{ height: 18, fontSize: '0.62rem',
                                  bgcolor: card.status === 'concluido' ? '#2e7d32' : card.status === 'em_andamento' ? '#1565c0' : card.status === 'bloqueado' ? '#b71c1c' : '#e65100',
                                  color: '#fff' }} />
                              {card.publicado && <Chip label="Publicado" size="small" sx={{ height: 18, fontSize: '0.62rem', bgcolor: '#43a047', color: '#fff' }} />}
                            </Box>
                            {(card.data_inicio || card.data_termino) && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <CalendarMonth sx={{ fontSize: 12, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">{fmtDate(card.data_inicio)} → {fmtDate(card.data_termino)}</Typography>
                              </Box>
                            )}
                            {card.categoria && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>📂 {card.categoria}</Typography>
                            )}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 0.5 }}>
                              <Tooltip title="Editar">
                                <IconButton size="small" onClick={() => openEditPrivado(card)}><Edit sx={{ fontSize: 14 }} /></IconButton>
                              </Tooltip>
                              <Tooltip title="Excluir">
                                <IconButton size="small" color="error" onClick={() => deletePrivado(card)}><Delete sx={{ fontSize: 14 }} /></IconButton>
                              </Tooltip>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Cards publicados por outros */}
                  {(privadoCards.publicados?.length > 0) && (
                    <>
                      <Divider sx={{ mb: 2 }} />
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1 }}>
                        Publicados por outros ({privadoCards.publicados.length})
                      </Typography>
                      <Grid container spacing={1.5}>
                        {privadoCards.publicados.map(card => (
                          <Grid item xs={12} sm={6} md={4} lg={3} key={card.id}>
                            <Card sx={{ borderLeft: `4px solid #43a047`, '&:hover': { boxShadow: 3 }, bgcolor: '#f1f8e9', opacity: 0.92 }}>
                              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>{card.titulo}</Typography>
                                  <Public sx={{ fontSize: 16, color: '#43a047', ml: 0.5 }} />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                  <Person sx={{ fontSize: 12, color: '#7b1fa2' }} />
                                  <Typography variant="caption" sx={{ color: '#7b1fa2', fontWeight: 600 }}>{card.usuario_nome}</Typography>
                                </Box>
                                {card.descricao && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    {card.descricao.length > 80 ? card.descricao.substring(0, 80) + '...' : card.descricao}
                                  </Typography>
                                )}
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                                  <Chip label={PRIORIDADE_OPTIONS.find(p => p.value === card.prioridade)?.label || card.prioridade} size="small"
                                    sx={{ height: 18, fontSize: '0.62rem', bgcolor: PRIORIDADE_CORES[card.prioridade] || '#ccc', color: '#fff' }} />
                                  <Chip label={STATUS_OPTIONS.find(s => s.value === card.status)?.label || card.status} size="small"
                                    sx={{ height: 18, fontSize: '0.62rem',
                                      bgcolor: card.status === 'concluido' ? '#2e7d32' : card.status === 'em_andamento' ? '#1565c0' : card.status === 'bloqueado' ? '#b71c1c' : '#e65100',
                                      color: '#fff' }} />
                                </Box>
                                {(card.data_inicio || card.data_termino) && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CalendarMonth sx={{ fontSize: 12, color: 'text.secondary' }} />
                                    <Typography variant="caption" color="text.secondary">{fmtDate(card.data_inicio)} → {fmtDate(card.data_termino)}</Typography>
                                  </Box>
                                )}
                                {card.categoria && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>📂 {card.categoria}</Typography>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}
                </>
              )}
            </>
          )}
          {/* ── Concluído ── */}
          {mainView === 'concluido' && (
            <>
              {/* Filtros */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  type="date" size="small" label="De" value={concluidoDataInicio}
                  onChange={(e) => { setConcluidoDataInicio(e.target.value); fetchConcluidos({ dataInicio: e.target.value }); }}
                  InputLabelProps={{ shrink: true }} sx={{ width: 155 }}
                />
                <TextField
                  type="date" size="small" label="Até" value={concluidoDataFim}
                  onChange={(e) => { setConcluidoDataFim(e.target.value); fetchConcluidos({ dataFim: e.target.value }); }}
                  InputLabelProps={{ shrink: true }} sx={{ width: 155 }}
                />
                <FormControl size="small" sx={{ minWidth: 250 }}>
                  <InputLabel>Loja</InputLabel>
                  <Select value={concluidoClienteId} label="Loja"
                    onChange={(e) => { setConcluidoClienteId(e.target.value); fetchConcluidos({ clienteId: e.target.value }); }}>
                    <MenuItem value="">Todas as lojas</MenuItem>
                    {clientes.map(c => <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>)}
                  </Select>
                </FormControl>
                <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {concluidoData.reduce((s, g) => s + g.cards.length, 0)} tarefas
                  </Typography>
                  <Button
                    variant="outlined" size="small" startIcon={<PictureAsPdf />}
                    onClick={exportConcluidoPdf}
                    disabled={concluidoData.length === 0}
                    sx={{ color: '#d63030', borderColor: '#d63030', '&:hover': { bgcolor: '#fff5f5', borderColor: '#b71c1c' } }}
                  >
                    Exportar PDF
                  </Button>
                </Box>
              </Box>

              {concluidoLoading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>}
              {!concluidoLoading && concluidoData.length === 0 && (
                <Alert severity="info">Nenhuma tarefa concluída encontrada com os filtros aplicados.</Alert>
              )}
              {!concluidoLoading && concluidoData.length > 0 && (
                <Box>
                  {concluidoData.sort((a, b) => lojaOrder(a.cliente_nome) - lojaOrder(b.cliente_nome)).map((grupo) => (
                    <Paper key={grupo.cliente_id} sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
                      <Box sx={{ px: 2, py: 1, bgcolor: '#1b5e20', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleOutline sx={{ color: '#a5d6a7', fontSize: 18 }} />
                        <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700 }}>{grupo.cliente_nome}</Typography>
                        <Chip label={grupo.cards.length} size="small" sx={{ bgcolor: '#2e7d32', color: '#fff', height: 20, fontSize: '0.7rem' }} />
                      </Box>
                      <Box sx={{ p: 1.5 }}>
                        <Grid container spacing={1}>
                          {grupo.cards.map((card) => (
                            <Grid item xs={12} sm={6} md={4} key={card.id}>
                              <Card sx={{ borderLeft: '4px solid #2e7d32', '&:hover': { boxShadow: 3 }, cursor: 'pointer' }} onClick={() => openEditCard(card)}>
                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>{card.titulo}</Typography>
                                  {card.descricao && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                      {card.descricao.length > 80 ? card.descricao.substring(0, 80) + '...' : card.descricao}
                                    </Typography>
                                  )}
                                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                                    {card.categoria && <Chip label={card.categoria} size="small" sx={{ height: 18, fontSize: '0.62rem' }} />}
                                    <Chip label="Concluído" size="small" sx={{ height: 18, fontSize: '0.62rem', bgcolor: '#2e7d32', color: '#fff' }} />
                                  </Box>
                                  {(card.data_inicio || card.data_termino) && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <CalendarMonth sx={{ fontSize: 12, color: 'text.secondary' }} />
                                      <Typography variant="caption" color="text.secondary">{fmtDate(card.data_inicio)} → {fmtDate(card.data_termino)}</Typography>
                                    </Box>
                                  )}
                                  {card.responsavel_nome && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                      <Person sx={{ fontSize: 12, color: 'text.secondary' }} />
                                      <Typography variant="caption" color="text.secondary">{card.responsavel_nome}</Typography>
                                    </Box>
                                  )}
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal criar/editar card */}
      <Dialog open={cardModalOpen} onClose={() => { setCardModalOpen(false); setEditingUser(null); setComentarioText(''); }} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6">{editingCard ? 'Editar Card' : 'Novo Card'}</Typography>
          {editingUser && <Chip label={`${editingUser.nome} editando`} size="small" color="warning" />}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', height: editingCard ? '70vh' : 'auto' }}>
            {/* ── Formulário (esquerda) ── */}
            <Box sx={{ flex: 1, p: 2.5, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField fullWidth label="Título *" value={cardForm.titulo} onChange={(e) => setCardForm({ ...cardForm, titulo: e.target.value })} />
                <TextField fullWidth multiline rows={3} label="Descrição" value={cardForm.descricao} onChange={(e) => setCardForm({ ...cardForm, descricao: e.target.value })} />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField select sx={{ flex: 1 }} label="Prioridade" value={cardForm.prioridade} onChange={(e) => setCardForm({ ...cardForm, prioridade: e.target.value })}>
                    {PRIORIDADE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </TextField>
                  <TextField select sx={{ flex: 1 }} label="Status" value={cardForm.status} onChange={(e) => setCardForm({ ...cardForm, status: e.target.value })}>
                    {STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </TextField>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField select sx={{ flex: 1 }} label="Categoria" value={cardForm.categoria} onChange={(e) => setCardForm({ ...cardForm, categoria: e.target.value })}>
                    <MenuItem value="">— Nenhuma —</MenuItem>
                    {CATEGORIAS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </TextField>
                  <TextField select sx={{ flex: 1 }} label="Responsável" value={cardForm.responsavel_id} onChange={(e) => setCardForm({ ...cardForm, responsavel_id: e.target.value })}>
                    <MenuItem value="">Nenhum</MenuItem>
                    {usuarios.map(u => <MenuItem key={u.id} value={u.id}>{u.nome}</MenuItem>)}
                  </TextField>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField sx={{ flex: 1 }} label={`Data Início${!editingCard ? ' *' : ''}`} type="date"
                    value={cardForm.data_inicio}
                    onChange={(e) => setCardForm(prev => ({ ...prev, data_inicio: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    error={!editingCard && !cardForm.data_inicio}
                    helperText={!editingCard && !cardForm.data_inicio ? 'Obrigatório' : ''}
                  />
                  <TextField sx={{ flex: 1 }} label={`Data Término${!editingCard ? ' *' : ''}`} type="date"
                    value={cardForm.data_termino}
                    onChange={(e) => setCardForm(prev => ({ ...prev, data_termino: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: cardForm.data_inicio || undefined }}
                    error={!editingCard && !cardForm.data_termino}
                    helperText={!editingCard && !cardForm.data_termino ? 'Obrigatório' : ''}
                  />
                </Box>
                {editingCard && (!editingCard.data_inicio || !editingCard.data_termino) && (
                  <Alert severity="warning" icon={<EditNote />} sx={{ py: 0.5, fontSize: '0.8rem' }}>
                    Este card ainda não tem datas definidas. Preencha para que apareça no Gantt.
                  </Alert>
                )}
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button onClick={() => { setCardModalOpen(false); setEditingUser(null); setComentarioText(''); }}>Cancelar</Button>
                  <Button variant="contained" startIcon={<Save />} onClick={saveCard}>Salvar</Button>
                </Box>
            </Box>

            {/* ── Histórico / Chat (direita) — só ao editar ── */}
            {editingCard && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ width: 340, display: 'flex', flexDirection: 'column', bgcolor: '#f8f9fa' }}>
                  {/* Header */}
                  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ChatBubbleOutline fontSize="small" color="action" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Histórico & Comentários</Typography>
                  </Box>

                  {/* Lista de entradas */}
                  <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {historicoLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>}
                    {!historicoLoading && historicoData.length === 0 && (
                      <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 2 }}>Nenhum histórico ainda.</Typography>
                    )}
                    {/* Renderiza do mais antigo ao mais recente (histórico vem desc) */}
                    {[...historicoData].reverse().map((h) => {
                      const isComentario = h.tipo === 'comentario';
                      const initials = (h.usuario_nome || '?').split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
                      const dt = new Date(h.created_at);
                      const dtStr = dt.toLocaleDateString('pt-BR') + ' ' + dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                      return (
                        <Box key={h.id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                          <Avatar sx={{ width: 28, height: 28, fontSize: '0.65rem', bgcolor: isComentario ? '#1565c0' : '#757575', flexShrink: 0, mt: 0.3 }}>
                            {initials}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.8, flexWrap: 'wrap' }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: isComentario ? '#1565c0' : '#37474f' }}>{h.usuario_nome}</Typography>
                              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem' }}>{dtStr}</Typography>
                            </Box>
                            {isComentario ? (
                              <Paper variant="outlined" sx={{ px: 1.5, py: 0.8, mt: 0.3, bgcolor: '#fff', borderColor: '#1565c0', borderRadius: 2 }}>
                                <Typography variant="body2" sx={{ fontSize: '0.82rem', whiteSpace: 'pre-wrap' }}>{fmtDetalhes(h.detalhes)}</Typography>
                              </Paper>
                            ) : (
                              <Box sx={{ px: 1, py: 0.4, mt: 0.2, bgcolor: '#eeeeee', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                                  <strong>{h.acao}</strong>{h.detalhes ? `: ${fmtDetalhes(h.detalhes)}` : ''}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Input de novo comentário */}
                  <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid #e0e0e0' }}>
                    <TextField
                      fullWidth multiline maxRows={4} size="small"
                      placeholder="Adicionar comentário..."
                      value={comentarioText}
                      onChange={(e) => setComentarioText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleAddComentario(); }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Enviar (Ctrl+Enter)">
                              <span>
                                <IconButton size="small" color="primary" onClick={handleAddComentario} disabled={!comentarioText.trim() || comentarioLoading}>
                                  {comentarioLoading ? <CircularProgress size={16} /> : <Send fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>Ctrl+Enter para enviar</Typography>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        {!editingCard && (
          <DialogActions>
            <Button onClick={() => { setCardModalOpen(false); setEditingUser(null); }}>Cancelar</Button>
            <Button variant="contained" startIcon={<Save />} onClick={saveCard}>Salvar</Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Modal criar/editar coluna */}
      <Dialog open={colunaModalOpen} onClose={() => setColunaModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingColuna ? 'Editar Coluna' : 'Nova Coluna'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Título *" value={colunaForm.titulo} onChange={(e) => setColunaForm({ ...colunaForm, titulo: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2">Cor:</Typography>
                <input type="color" value={colunaForm.cor} onChange={(e) => setColunaForm({ ...colunaForm, cor: e.target.value })} style={{ width: 40, height: 30, border: 'none', cursor: 'pointer' }} />
                <Box sx={{ width: 60, height: 30, borderRadius: 1, bgcolor: colunaForm.cor, border: '1px solid #ccc' }} />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setColunaModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveColuna}>Salvar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal histórico */}
      <Dialog open={historicoModalOpen} onClose={() => setHistoricoModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <History />
            <Typography variant="h6">Histórico de Alterações</Typography>
          </Box>
          <IconButton onClick={() => setHistoricoModalOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {historicoLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
          ) : historicoData.length === 0 ? (
            <Alert severity="info">Nenhuma alteração registrada.</Alert>
          ) : (
            <List dense>
              {historicoData.map((h) => (
                <ListItem key={h.id} sx={{ borderBottom: '1px solid #f0f0f0' }}>
                  <ListItemIcon sx={{ minWidth: 36 }}><Person fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="body2"><strong>{h.usuario_nome}</strong> {h.acao}</Typography>}
                    secondary={
                      <>
                        <Typography variant="caption" color="text.secondary">{fmtDetalhes(h.detalhes)}</Typography>
                        <br />
                        <Typography variant="caption" color="text.disabled">{new Date(h.created_at).toLocaleString('pt-BR')}</Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal criar/editar card privado */}
      <Dialog open={privadoModalOpen} onClose={() => setPrivadoModalOpen(false)}
        maxWidth={editingPrivado ? 'md' : 'sm'} fullWidth
        PaperProps={{ sx: { height: editingPrivado ? '80vh' : 'auto' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#7b1fa2', color: '#fff', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {editingPrivado ? <LockOpen fontSize="small" /> : <Lock fontSize="small" />}
            <Typography variant="h6">{editingPrivado ? 'Editar Card Privado' : 'Novo Card Privado'}</Typography>
          </Box>
          <IconButton onClick={() => setPrivadoModalOpen(false)} sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, display: 'flex', overflow: 'hidden' }}>
          {/* ── Formulário (esquerda) ── */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField fullWidth label="Título *" value={privadoForm.titulo}
              onChange={(e) => setPrivadoForm({ ...privadoForm, titulo: e.target.value })} autoFocus />
            <TextField fullWidth multiline rows={3} label="Descrição" value={privadoForm.descricao}
              onChange={(e) => setPrivadoForm({ ...privadoForm, descricao: e.target.value })} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField select sx={{ flex: 1 }} label="Prioridade" value={privadoForm.prioridade}
                onChange={(e) => setPrivadoForm({ ...privadoForm, prioridade: e.target.value })}>
                {PRIORIDADE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
              <TextField select sx={{ flex: 1 }} label="Status" value={privadoForm.status}
                onChange={(e) => setPrivadoForm({ ...privadoForm, status: e.target.value })}>
                {STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Box>
            <TextField select fullWidth label="Categoria" value={privadoForm.categoria}
              onChange={(e) => setPrivadoForm({ ...privadoForm, categoria: e.target.value })}>
              <MenuItem value="">— Nenhuma —</MenuItem>
              {CATEGORIAS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField sx={{ flex: 1 }} label="Data Início" type="date" value={privadoForm.data_inicio}
                onChange={(e) => setPrivadoForm({ ...privadoForm, data_inicio: e.target.value })}
                InputLabelProps={{ shrink: true }} />
              <TextField sx={{ flex: 1 }} label="Data Término" type="date" value={privadoForm.data_termino}
                onChange={(e) => setPrivadoForm({ ...privadoForm, data_termino: e.target.value })}
                InputLabelProps={{ shrink: true }} inputProps={{ min: privadoForm.data_inicio || undefined }} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 'auto', pt: 1 }}>
              <Button onClick={() => setPrivadoModalOpen(false)}>Cancelar</Button>
              <Button variant="contained" startIcon={<Save />} onClick={savePrivado}
                sx={{ bgcolor: '#7b1fa2', '&:hover': { bgcolor: '#6a1b9a' } }}>Salvar</Button>
            </Box>
          </Box>

          {/* ── Comentários (direita) — só ao editar ── */}
          {editingPrivado && (
            <>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ width: 320, display: 'flex', flexDirection: 'column', bgcolor: '#f8f9fa' }}>
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ChatBubbleOutline fontSize="small" color="action" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Comentários</Typography>
                </Box>
                <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {privadoComentariosLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>}
                  {!privadoComentariosLoading && privadoComentarios.length === 0 && (
                    <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 2, display: 'block' }}>Nenhum comentário ainda.</Typography>
                  )}
                  {privadoComentarios.map((c) => {
                    const initials = (c.usuario_nome || '?').split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
                    const dt = new Date(c.created_at);
                    const dtStr = dt.toLocaleDateString('pt-BR') + ' ' + dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                    return (
                      <Box key={c.id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: '0.65rem', bgcolor: '#7b1fa2', flexShrink: 0, mt: 0.3 }}>{initials}</Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.8, flexWrap: 'wrap' }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#7b1fa2' }}>{c.usuario_nome}</Typography>
                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem' }}>{dtStr}</Typography>
                          </Box>
                          <Paper variant="outlined" sx={{ px: 1.5, py: 0.8, mt: 0.3, bgcolor: '#fff', borderColor: '#7b1fa2', borderRadius: 2 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.82rem', whiteSpace: 'pre-wrap' }}>{c.texto}</Typography>
                          </Paper>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
                <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid #e0e0e0' }}>
                  <TextField
                    fullWidth multiline maxRows={4} size="small"
                    placeholder="Adicionar comentário..."
                    value={privadoComentarioText}
                    onChange={(e) => setPrivadoComentarioText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleAddPrivadoComentario(); }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Enviar (Ctrl+Enter)">
                            <span>
                              <IconButton size="small" onClick={handleAddPrivadoComentario}
                                disabled={!privadoComentarioText.trim() || privadoComentarioSending}
                                sx={{ color: '#7b1fa2' }}>
                                {privadoComentarioSending ? <CircularProgress size={16} /> : <Send fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>Ctrl+Enter para enviar</Typography>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ ...snack, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>{snack.message}</Alert>
      </Snackbar>

      {/* ── Modal Duplicar em Lote ── */}
      <Dialog open={duplicarModalOpen} onClose={() => !duplicarLoading && setDuplicarModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ContentCopy fontSize="small" />
          Duplicar tarefa para clientes
        </DialogTitle>
        <DialogContent dividers>
          {duplicarCard && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={600}>{duplicarCard.titulo}</Typography>
              {duplicarCard.descricao && <Typography variant="caption" color="text.secondary">{duplicarCard.descricao}</Typography>}
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {duplicarClienteIds.size} cliente(s) selecionado(s)
            </Typography>
            <Button size="small" onClick={() => {
              if (duplicarClienteIds.size === clientes.length) {
                setDuplicarClienteIds(new Set());
              } else {
                setDuplicarClienteIds(new Set(clientes.map(c => c.id)));
              }
            }}>
              {duplicarClienteIds.size === clientes.length ? 'Desmarcar todos' : 'Selecionar todos'}
            </Button>
          </Box>
          <Box sx={{ maxHeight: 350, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {clientes.map(c => (
              <Box key={c.id} sx={{ borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                <FormControlLabel
                  sx={{ m: 0, px: 1.5, py: 0.5, width: '100%' }}
                  control={
                    <Checkbox
                      size="small"
                      checked={duplicarClienteIds.has(c.id)}
                      onChange={() => toggleDuplicarCliente(c.id)}
                      disabled={duplicarLoading}
                    />
                  }
                  label={<Typography variant="body2">{c.nome}</Typography>}
                />
              </Box>
            ))}
          </Box>
          {duplicarLoading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={duplicarProgress.total > 0 ? (duplicarProgress.done / duplicarProgress.total) * 100 : 0} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
                Processando {duplicarProgress.done}/{duplicarProgress.total}...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicarModalOpen(false)} disabled={duplicarLoading}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={executarDuplicacao}
            disabled={duplicarClienteIds.size === 0 || duplicarLoading}
            startIcon={duplicarLoading ? <CircularProgress size={16} color="inherit" /> : <ContentCopy />}
          >
            {duplicarLoading ? 'Duplicando...' : `Duplicar para ${duplicarClienteIds.size} cliente(s)`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
